import { useState, useEffect } from 'react';
import { collection, addDoc, doc, updateDoc, getDoc, onSnapshot, query, where, serverTimestamp, writeBatch, increment } from 'firebase/firestore';
import { db, addAuditLog } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/PageHeader';
import styles from './BranchTransactions.module.css';
import { formatOrderId, formatTransactionId } from '../../utils/formatOrderId';
import juanCafeLogo from '../../assets/JuanCafeLogo.jpg';

function fmt(n) { return '₱' + n.toLocaleString(); }

function formatDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toISOString().slice(0, 10);
}

function StatusBadge({ status }) {
  const cls = status === 'Verified' ? styles.badgeVerified
    : status === 'Settled' ? styles.badgeSettled
    : styles.badgePending;
  return <span className={`${styles.badge} ${cls}`}>{status}</span>;
}

export default function BranchTransactions() {
  const { user }                            = useAuth();
  const [pendingOrder, setPendingOrder]     = useState(null);
  const [myTransactions, setMyTransactions] = useState([]);
  const [method, setMethod]                 = useState(null);
  const [fullPaySub, setFullPaySub]         = useState(null); // 'cash' | 'gcash'
  const [downPayPct, setDownPayPct]         = useState(50);
  const [gcashRef, setGcashRef]             = useState('');
  const [showModal, setShowModal]           = useState(false);
  const [confirmed, setConfirmed]           = useState(false);
  const [confirmedMethod, setConfirmedMethod]   = useState('');
  const [confirmedPartial, setConfirmedPartial] = useState(0);
  const [confirmedRemaining, setConfirmedRemaining] = useState(0);
  const [confirmedOrderId, setConfirmedOrderId] = useState('');
  const [confirmedLineItems, setConfirmedLineItems] = useState([]);
  const [confirmedSubtotal, setConfirmedSubtotal] = useState(0);
  const [confirmedDeliveryFee, setConfirmedDeliveryFee] = useState(0);
  const [confirmedTotal, setConfirmedTotal] = useState(0);
  const [confirmedGcashRef, setConfirmedGcashRef] = useState('');
  const [confirmedTimestamp, setConfirmedTimestamp] = useState(null);
  const [confirmedTransactionId, setConfirmedTransactionId] = useState('');
  const [showReceipt, setShowReceipt]       = useState(false);
  const [historyReceiptTx, setHistoryReceiptTx] = useState(null); // tx from history to show
  const [historyReceiptOrder, setHistoryReceiptOrder] = useState(null); // fetched order for that tx
  const [submitting, setSubmitting]         = useState(false);
  const [error, setError]                   = useState('');
  const [selectedBalance, setSelectedBalance]   = useState(null); // tx._docId
  const [balanceGcashRef, setBalanceGcashRef]   = useState('');
  const [submittingBalance, setSubmittingBalance] = useState(false);
  const [balanceError, setBalanceError]         = useState('');
  const [balanceConfirmedId, setBalanceConfirmedId] = useState(null);

  // Load pending orders for this branch that haven't been paid yet
  useEffect(() => {
    if (!user?.uid) return;
    const q = query(
      collection(db, 'orders'),
      where('branchId', '==', user.uid),
      where('status', '==', 'Pending')
    );
    const unsub = onSnapshot(q, snap => {
      const docs = snap.docs.map(d => ({ _docId: d.id, ...d.data() }));
      docs.sort((a, b) => {
        const at = a.createdAt?.toMillis?.() ?? 0;
        const bt = b.createdAt?.toMillis?.() ?? 0;
        return bt - at;
      });
      setPendingOrder(docs[0] ?? null);
    });
    return unsub;
  }, [user?.uid]);

  // Load this branch's transaction history
  useEffect(() => {
    if (!user?.uid) return;
    const q = query(
      collection(db, 'transactions'),
      where('branchId', '==', user.uid)
    );
    const unsub = onSnapshot(q, snap => {
      const docs = snap.docs.map(d => ({ _docId: d.id, ...d.data() }));
      docs.sort((a, b) => {
        const at = a.createdAt?.toMillis?.() ?? 0;
        const bt = b.createdAt?.toMillis?.() ?? 0;
        return bt - at;
      });
      setMyTransactions(docs);
    });
    return unsub;
  }, [user?.uid]);

  // Use the total already stored on the order — no hardcoded fee constants
  const lineItems = (pendingOrder?.items || []).map(i => ({
    name: i.productId, qty: i.qty, unit: i.price, subtotal: i.qty * i.price,
  }));
  const subtotal = lineItems.reduce((s, i) => s + i.subtotal, 0);
  const total    = pendingOrder?.total ?? subtotal;
  const deliveryFee = total - subtotal;

  const partialAmount   = Math.round(total * (downPayPct / 100));
  const remainingAmount = total - partialAmount;
  const orderId = pendingOrder?._docId ?? '—';

  // True if a transaction already exists for the current pending order
  const alreadyPaid = pendingOrder
    ? myTransactions.some(tx => tx.orderId === pendingOrder._docId)
    : false;

  // Outstanding partial payment balances not yet settled
  const outstandingBalances = myTransactions.filter(
    tx => tx.type === 'Partial Payment' && tx.remainingAmount > 0 && tx.status !== 'Settled'
  );

  function handleMethodSelect(m) {
    setMethod(m);
    setFullPaySub(null);
    setGcashRef('');
  }

  function handleFullPaySubSelect(sub) {
    setFullPaySub(sub);
    if (sub !== 'gcash') setGcashRef('');
  }

  const isGcash = method === 'full' && fullPaySub === 'gcash';
  const needsGcashRef = isGcash || method === 'partial';

  function handleConfirmClick() {
    if (!method) { setError('Please select a payment method before proceeding.'); return; }
    if (method === 'full' && !fullPaySub) { setError('Please select Cash on Delivery or GCash to continue.'); return; }
    if (needsGcashRef && !gcashRef.trim()) { setError('Please enter your GCash reference number before proceeding.'); return; }
    setError('');
    setShowModal(true);
  }

  async function handleConfirm() {
    setShowModal(false);
    setSubmitting(true);
    setError('');
    try {
      const txType = method === 'partial' ? 'Partial Payment' : isGcash ? 'GCash' : 'Cash';

      // 1. Create transaction record — branch has write access to transactions collection
      await addDoc(collection(db, 'transactions'), {
        orderId,
        branchId:         user.uid,
        branchName:       user.name,
        amount:           total,
        type:             txType,
        partialAmount:    method === 'partial' ? partialAmount   : total,
        remainingAmount:  method === 'partial' ? remainingAmount : 0,
        paymentDue:       null,
        status:           'Pending',
        gcashRefNumber:   needsGcashRef ? gcashRef.trim() : null,
        createdAt:        serverTimestamp(),
      });

      // 2. Update order with payment method and advance status
      if (pendingOrder?._docId) {
        await updateDoc(doc(db, 'orders', pendingOrder._docId), {
          paymentMethod: txType,
          status:        'Confirmed',
          updatedAt:     serverTimestamp(),
        });
      }

      // 4. Deduct inventory for each item in the order (atomic batch)
      const orderItems = pendingOrder?.items ?? [];
      if (orderItems.length > 0) {
        const stockBatch = writeBatch(db);
        for (const item of orderItems) {
          if (item.productId && item.qty > 0) {
            stockBatch.update(doc(db, 'products', item.productId), { stock: increment(-item.qty) });
          }
        }
        await stockBatch.commit();
      }

      await addAuditLog(
        user.uid, user.name,
        `Submitted ${txType} for order ${orderId}`,
        'Transactions',
      );
      const now = new Date();
      const displayMethod = method === 'partial' ? 'Partial Payment' : isGcash ? 'GCash' : 'Cash on Delivery';
      setConfirmedMethod(displayMethod);
      setConfirmedPartial(method === 'partial' ? partialAmount : total);
      setConfirmedRemaining(method === 'partial' ? remainingAmount : 0);
      setConfirmedOrderId(orderId);
      setConfirmedLineItems([...lineItems]);
      setConfirmedSubtotal(subtotal);
      setConfirmedDeliveryFee(deliveryFee);
      setConfirmedTotal(total);
      setConfirmedGcashRef(needsGcashRef ? gcashRef.trim() : '');
      setConfirmedTimestamp(now);
      setConfirmedTransactionId(`TRN-${now.toISOString().slice(0,10).replace(/-/g,'')}-${orderId.slice(-4).toUpperCase()}`);
      setConfirmed(true);
    } catch (e) {
      console.error('Submit payment failed:', e);
      setError('Failed to submit payment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleBalanceSubmit(tx) {
    if (!balanceGcashRef.trim()) { setBalanceError('Please enter your GCash reference number.'); return; }
    setBalanceError('');
    setSubmittingBalance(true);
    try {
      await updateDoc(doc(db, 'transactions', tx._docId), {
        balanceGcashRef:      balanceGcashRef.trim(),
        balanceSubmittedAt:   serverTimestamp(),
      });
      await addAuditLog(
        user.uid, user.name,
        `Submitted balance GCash payment for order ${tx.orderId}`,
        'Transactions',
      );
      setBalanceConfirmedId(tx._docId);
      setSelectedBalance(null);
      setBalanceGcashRef('');
    } catch (e) {
      console.error('Balance payment failed:', e);
      setBalanceError('Failed to submit. Please try again.');
    } finally {
      setSubmittingBalance(false);
    }
  }

  function buildReceiptHTML({ branchName, orderIdStr, transactionId, date, time, payment, gcashRef, lineItems, vatableBase, vatAmount, itemsSubtotal, deliveryFee, total, partial, remaining, method, logoDataUrl }) {
    const fmtNum = n => '₱' + Number(n).toLocaleString();
    const rows = lineItems.map(item => `
      <tr>
        <td style="padding:8px 12px;border-bottom:1px solid #f0e8df;">${item.name}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #f0e8df;text-align:center;">${item.qty}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #f0e8df;text-align:right;">${fmtNum(item.unit)}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #f0e8df;text-align:right;">${fmtNum(item.subtotal)}</td>
      </tr>`).join('');
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Receipt - ${transactionId}</title>
    <style>
      body{font-family:'Helvetica Neue',Arial,sans-serif;background:#f5ede6;margin:0;padding:40px 20px;color:#3b1c0c;}
      .receipt{background:#fff;max-width:680px;margin:0 auto;border-radius:16px;padding:40px 48px;box-shadow:0 4px 24px rgba(91,45,18,0.1);}
      .header{display:flex;align-items:center;gap:20px;padding-bottom:20px;border-bottom:1px solid #e8d5c4;}
      .company-name{font-size:22px;font-weight:800;letter-spacing:2px;color:#5b2d12;margin:0 0 4px;}
      .company-meta{font-size:12px;color:#8b6347;margin:2px 0;}
      .doc-type{text-align:center;margin:24px 0 20px;}
      .doc-label{font-size:17px;font-weight:800;letter-spacing:3px;color:#5b2d12;border-bottom:2px solid #5b2d12;padding-bottom:4px;}
      .doc-sub{display:block;font-size:12px;color:#8b6347;margin-top:6px;}
      .meta{display:grid;grid-template-columns:1fr 1fr;gap:8px 24px;margin-bottom:20px;}
      .meta-row{display:flex;gap:8px;font-size:12px;}
      .meta-key{color:#8b6347;text-transform:uppercase;letter-spacing:.5px;white-space:nowrap;}
      .meta-val{font-weight:700;color:#3b1c0c;}
      table{width:100%;border-collapse:collapse;margin-bottom:20px;}
      thead tr{background:#5b2d12;color:#fff;}
      th{padding:10px 12px;text-align:left;font-size:12px;letter-spacing:.5px;}
      th:nth-child(2){text-align:center;}
      th:nth-child(3),th:nth-child(4){text-align:right;}
      td{font-size:13px;color:#3b1c0c;}
      .totals{margin-left:auto;width:280px;border-top:1px solid #e8d5c4;padding-top:12px;}
      .tot-row{display:flex;justify-content:space-between;font-size:13px;padding:4px 0;color:#5b2d12;}
      .grand{font-size:15px;font-weight:800;border-top:2px solid #5b2d12;padding-top:8px;margin-top:4px;}
      .footer{margin-top:28px;border-top:1px dashed #e8d5c4;padding-top:20px;text-align:center;}
      .footer-note{font-size:11px;color:#8b6347;margin:3px 0;font-style:italic;}
      .footer-thank{font-size:14px;color:#5b2d12;font-weight:600;margin:12px 0;}
      .sig-block{display:flex;justify-content:space-around;margin-top:24px;}
      .sig{text-align:center;}
      .sig-line{width:160px;border-bottom:1px solid #3b1c0c;margin-bottom:6px;}
      .sig-label{font-size:11px;color:#8b6347;}
      .status-badge{display:inline-block;margin-top:16px;font-size:12px;padding:5px 16px;border-radius:999px;background:#fef3c7;border:1px solid #f59e0b;color:#92400e;}
      @media print{body{background:#fff;padding:0;}@page{margin:20mm;}}
    </style></head><body>
    <div class="receipt">
      <div class="header">
        ${logoDataUrl ? `<img src="${logoDataUrl}" alt="Juan Cafe Logo" style="height:48px;object-fit:contain;" />` : ''}
        <div>
          <div class="company-name">JUAN CAFE</div>
          <div class="company-meta">Supply Chain Management System</div>
          <div class="company-meta">Bukidnon, Philippines</div>
          <div class="company-meta">TIN: 000-000-000-000</div>
        </div>
      </div>
      <div class="doc-type">
        <span class="doc-label">OFFICIAL RECEIPT</span>
        <span class="doc-sub">(Supply Invoice)</span>
      </div>
      <div class="meta">
        <div>
          <div class="meta-row"><span class="meta-key">Sold To:&nbsp;</span><span class="meta-val">${branchName}</span></div>
          <div class="meta-row"><span class="meta-key">Order No.:&nbsp;</span><span class="meta-val">${orderIdStr}</span></div>
          <div class="meta-row"><span class="meta-key">Transaction No.:&nbsp;</span><span class="meta-val">${transactionId}</span></div>
        </div>
        <div>
          <div class="meta-row"><span class="meta-key">Date:&nbsp;</span><span class="meta-val">${date}</span></div>
          <div class="meta-row"><span class="meta-key">Time:&nbsp;</span><span class="meta-val">${time}</span></div>
          <div class="meta-row"><span class="meta-key">Payment:&nbsp;</span><span class="meta-val">${payment}</span></div>
          ${gcashRef ? `<div class="meta-row"><span class="meta-key">GCash Ref.:&nbsp;</span><span class="meta-val">${gcashRef}</span></div>` : ''}
        </div>
      </div>
      <table>
        <thead><tr><th>Description</th><th>Qty</th><th>Unit Price</th><th>Amount</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="totals">
        <div class="tot-row"><span>Subtotal (VATable)</span><span>${fmtNum(Math.round(vatableBase))}</span></div>
        <div class="tot-row"><span>VAT (12%)</span><span>${fmtNum(Math.round(vatAmount))}</span></div>
        <div class="tot-row"><span>Goods Total</span><span>${fmtNum(itemsSubtotal)}</span></div>
        ${deliveryFee > 0 ? `<div class="tot-row"><span>Delivery Fee</span><span>${fmtNum(deliveryFee)}</span></div>` : ''}
        <div class="tot-row grand"><span>TOTAL AMOUNT DUE</span><span>${fmtNum(total)}</span></div>
        ${method === 'Partial Payment' ? `
          <div class="tot-row" style="color:#16a34a;"><span>Amount Paid (Upon Arrival)</span><span>${fmtNum(partial)}</span></div>
          <div class="tot-row" style="color:#dc2626;"><span>Remaining Balance</span><span>${fmtNum(remaining)}</span></div>` : ''}
      </div>
      <div class="footer">
        <p class="footer-note">This document is not valid for claiming input taxes.</p>
        <p class="footer-note">This serves as your Official Receipt for the above transaction.</p>
        <p class="footer-thank">Thank you for your business!</p>
        <div class="sig-block">
          <div class="sig"><div class="sig-line"></div><span class="sig-label">Prepared by / Cashier</span></div>
          <div class="sig"><div class="sig-line"></div><span class="sig-label">Received by / Branch</span></div>
        </div>
        <div><span class="status-badge">Status: Pending Admin Verification</span></div>
      </div>
    </div>
    <script>window.onload=()=>window.print();</script>
    </body></html>`;
  }

  async function handleExport(params) {
    let logoDataUrl = '';
    try {
      const resp = await fetch(juanCafeLogo);
      const buffer = await resp.arrayBuffer();
      const base64 = btoa(String.fromCharCode(...new Uint8Array(buffer)));
      const mime = resp.headers.get('content-type') || 'image/jpeg';
      logoDataUrl = `data:${mime};base64,${base64}`;
    } catch (e) {
      console.warn('Could not embed logo:', e);
    }
    const html = buildReceiptHTML({ ...params, logoDataUrl });
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, '_blank');
    if (w) setTimeout(() => URL.revokeObjectURL(url), 10000);
  }

  async function handleViewHistoryReceipt(tx) {
    setHistoryReceiptTx(tx);
    setHistoryReceiptOrder(null);
    try {
      const snap = await getDoc(doc(db, 'orders', tx.orderId));
      if (snap.exists()) setHistoryReceiptOrder({ _docId: snap.id, ...snap.data() });
    } catch (e) {
      console.error('Failed to fetch order for receipt:', e);
    }
  }

  if (confirmed) {
    const outstandingAfter = myTransactions.filter(
      tx => tx.type === 'Partial Payment' && tx.remainingAmount > 0 && tx.status !== 'Settled'
    );

    const vatRate = 0.12;
    const vatableBase = confirmedSubtotal / (1 + vatRate);
    const vatAmount   = confirmedSubtotal - vatableBase;

    const tsDate = confirmedTimestamp
      ? confirmedTimestamp.toLocaleDateString('en-PH', { year: 'numeric', month: 'long', day: 'numeric' })
      : '—';
    const tsTime = confirmedTimestamp
      ? confirmedTimestamp.toLocaleTimeString('en-PH', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })
      : '—';

    const exportParams = {
      branchName: user.name,
      orderIdStr: formatOrderId(confirmedOrderId),
      transactionId: confirmedTransactionId,
      date: tsDate,
      time: tsTime,
      payment: confirmedMethod,
      gcashRef: confirmedGcashRef,
      lineItems: confirmedLineItems,
      vatableBase,
      vatAmount,
      itemsSubtotal: confirmedSubtotal,
      deliveryFee: confirmedDeliveryFee,
      total: confirmedTotal,
      partial: confirmedPartial,
      remaining: confirmedRemaining,
      method: confirmedMethod,
    };

    return (
      <div className={styles.page}>
        <PageHeader title="Payment" description="Complete your supply order payment" />

        {/* ── Success screen ── */}
        {!showReceipt ? (
          <div className={styles.card} style={{ alignItems: 'center', gap: '20px', padding: '36px 28px' }}>
            <div className={styles.successIcon}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/>
              </svg>
            </div>
            <h2 className={styles.sectionTitle} style={{ margin: 0 }}>Transaction Recorded</h2>
            <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '14px', margin: 0 }}>
              Your payment for Order <strong>{formatOrderId(confirmedOrderId)}</strong> has been recorded.<br />
              Transaction No.: <strong>{confirmedTransactionId}</strong>
            </p>
            <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px', margin: 0, fontStyle: 'italic' }}>
              Awaiting admin verification.
            </p>
            <div className={styles.receiptActions} style={{ borderTop: 'none', paddingTop: 0, marginTop: '8px' }}>
              <button className={styles.printBtn} onClick={() => setShowReceipt(true)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6, verticalAlign: 'middle' }}>
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
                </svg>
                View Receipt
              </button>
              <button className={styles.exportBtn} onClick={() => handleExport(exportParams)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6, verticalAlign: 'middle' }}>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Export
              </button>
              <button className={styles.backBtn} onClick={() => { setConfirmed(false); setShowReceipt(false); setMethod(null); }}>Done</button>
            </div>
          </div>
        ) : (
          /* ── Official Receipt ── */
          <div className={`${styles.receipt} printArea`} id="receipt">

            {/* Header */}
            <div className={styles.receiptHeader}>
              <div className={styles.receiptLogo}>
                <img src={juanCafeLogo} alt="Juan Cafe Logo" className={styles.receiptLogoImg} />
              </div>
              <div className={styles.receiptCompany}>
                <p className={styles.receiptCompanyName}>JUAN CAFE</p>
                <p className={styles.receiptCompanyMeta}>Supply Chain Management System</p>
                <p className={styles.receiptCompanyMeta}>Bukidnon, Philippines</p>
                <p className={styles.receiptCompanyMeta}>TIN: 000-000-000-000</p>
              </div>
            </div>

            <div className={styles.receiptDocType}>
              <span className={styles.receiptDocLabel}>OFFICIAL RECEIPT</span>
              <span className={styles.receiptDocSub}>(Supply Invoice)</span>
            </div>

            {/* Meta grid */}
            <div className={styles.receiptMeta}>
              <div className={styles.receiptMetaLeft}>
                <div className={styles.receiptMetaRow}>
                  <span className={styles.receiptMetaKey}>Sold To:</span>
                  <span className={styles.receiptMetaVal}>{user.name}</span>
                </div>
                <div className={styles.receiptMetaRow}>
                  <span className={styles.receiptMetaKey}>Order No.:</span>
                  <span className={styles.receiptMetaVal}>{formatOrderId(confirmedOrderId)}</span>
                </div>
                <div className={styles.receiptMetaRow}>
                  <span className={styles.receiptMetaKey}>Transaction No.:</span>
                  <span className={styles.receiptMetaVal}>{confirmedTransactionId}</span>
                </div>
              </div>
              <div className={styles.receiptMetaRight}>
                <div className={styles.receiptMetaRow}>
                  <span className={styles.receiptMetaKey}>Date:</span>
                  <span className={styles.receiptMetaVal}>{tsDate}</span>
                </div>
                <div className={styles.receiptMetaRow}>
                  <span className={styles.receiptMetaKey}>Time:</span>
                  <span className={styles.receiptMetaVal}>{tsTime}</span>
                </div>
                <div className={styles.receiptMetaRow}>
                  <span className={styles.receiptMetaKey}>Payment:</span>
                  <span className={styles.receiptMetaVal}>{confirmedMethod}</span>
                </div>
                {confirmedGcashRef && (
                  <div className={styles.receiptMetaRow}>
                    <span className={styles.receiptMetaKey}>GCash Ref.:</span>
                    <span className={styles.receiptMetaVal}>{confirmedGcashRef}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Items table */}
            <table className={styles.receiptTable}>
              <thead>
                <tr>
                  <th className={styles.receiptTh}>Description</th>
                  <th className={`${styles.receiptTh} ${styles.receiptThCenter}`}>Qty</th>
                  <th className={`${styles.receiptTh} ${styles.receiptThRight}`}>Unit Price</th>
                  <th className={`${styles.receiptTh} ${styles.receiptThRight}`}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {confirmedLineItems.map((item, i) => (
                  <tr key={i} className={i % 2 === 0 ? styles.receiptRowEven : styles.receiptRowOdd}>
                    <td className={styles.receiptTd}>{item.name}</td>
                    <td className={`${styles.receiptTd} ${styles.receiptTdCenter}`}>{item.qty}</td>
                    <td className={`${styles.receiptTd} ${styles.receiptTdRight}`}>{fmt(item.unit)}</td>
                    <td className={`${styles.receiptTd} ${styles.receiptTdRight}`}>{fmt(item.subtotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Totals */}
            <div className={styles.receiptTotals}>
              <div className={styles.receiptTotalRow}>
                <span>Subtotal (VATable)</span>
                <span>{fmt(Math.round(vatableBase))}</span>
              </div>
              <div className={styles.receiptTotalRow}>
                <span>VAT (12%)</span>
                <span>{fmt(Math.round(vatAmount))}</span>
              </div>
              <div className={styles.receiptTotalRow}>
                <span>Goods Total</span>
                <span>{fmt(confirmedSubtotal)}</span>
              </div>
              {confirmedDeliveryFee > 0 && (
                <div className={styles.receiptTotalRow}>
                  <span>Delivery Fee</span>
                  <span>{fmt(confirmedDeliveryFee)}</span>
                </div>
              )}
              <div className={`${styles.receiptTotalRow} ${styles.receiptGrandTotal}`}>
                <span>TOTAL AMOUNT DUE</span>
                <span>{fmt(confirmedTotal)}</span>
              </div>
              {confirmedMethod === 'Partial Payment' && (
                <>
                  <div className={`${styles.receiptTotalRow} ${styles.receiptPaidRow}`}>
                    <span>Amount Paid (Upon Arrival)</span>
                    <span>{fmt(confirmedPartial)}</span>
                  </div>
                  <div className={`${styles.receiptTotalRow} ${styles.receiptBalanceRow}`}>
                    <span>Remaining Balance</span>
                    <span>{fmt(confirmedRemaining)}</span>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className={styles.receiptFooter}>
              <p className={styles.receiptFooterNote}>
                This document is not valid for claiming input taxes.
              </p>
              <p className={styles.receiptFooterNote}>
                This serves as your Official Receipt for the above transaction.
              </p>
              <p className={styles.receiptFooterThank}>Thank you for your business!</p>
              <div className={styles.receiptSigBlock}>
                <div className={styles.receiptSig}>
                  <div className={styles.receiptSigLine} />
                  <span className={styles.receiptSigLabel}>Prepared by / Cashier</span>
                </div>
                <div className={styles.receiptSig}>
                  <div className={styles.receiptSigLine} />
                  <span className={styles.receiptSigLabel}>Received by / Branch</span>
                </div>
              </div>
              <p className={styles.receiptStatus}>
                Status: <strong>Pending Admin Verification</strong>
              </p>
            </div>

            {/* Actions */}
            <div className={styles.receiptActions}>
              <button className={styles.exportBtn} onClick={() => handleExport(exportParams)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6, verticalAlign: 'middle' }}>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Export
              </button>
              <button className={styles.backBtn} onClick={() => setShowReceipt(false)}>Back</button>
            </div>
          </div>
        )}

        {outstandingAfter.length > 0 && (
          <div className={styles.card} style={{ borderLeft: '4px solid #e07b39' }}>
            <h2 className={styles.sectionTitle} style={{ color: '#e07b39' }}>Outstanding Balance</h2>
            {outstandingAfter.map(tx => {
              const alreadySubmitted = !!tx.balanceGcashRef;
              const isSelected = selectedBalance === tx._docId;
              const justConfirmed = balanceConfirmedId === tx._docId;
              return (
                <div key={tx._docId} style={{ marginBottom: '1rem' }}>
                  <div className={styles.netRow} style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '0.25rem' }}>
                    <span style={{ fontWeight: 600 }}>Order {formatOrderId(tx.orderId)}</span>
                    <span className={styles.netLabel}>Remaining balance: <strong>{fmt(tx.remainingAmount)}</strong></span>
                    {tx.paymentDue
                      ? <span className={styles.netLabel}>Due date: <strong>{tx.paymentDue}</strong></span>
                      : <span className={styles.netLabel} style={{ fontStyle: 'italic' }}>Due date pending admin review</span>
                    }
                    <span className={styles.netLabel}>Status: <strong>{tx.status}</strong></span>
                  </div>
                  {justConfirmed && (
                    <p style={{ color: 'green', marginTop: '0.5rem', fontSize: '14px' }}>✓ Balance payment submitted. Awaiting admin verification.</p>
                  )}
                  {!alreadySubmitted && !justConfirmed && (
                    <>
                      {!isSelected ? (
                        <button className={styles.confirmBtn} style={{ marginTop: '0.75rem', width: 'auto', padding: '0.5rem 1.25rem' }} onClick={() => { setSelectedBalance(tx._docId); setBalanceGcashRef(''); setBalanceError(''); }}>
                          Pay Remaining Balance
                        </button>
                      ) : (
                        <div style={{ marginTop: '1rem' }}>
                          <p className={styles.gcashInstruction}>Scan the QR code below using your GCash app to send the remaining balance of <strong>{fmt(tx.remainingAmount)}</strong>. Then enter your GCash reference number below.</p>
                          <div className={styles.gcashQrWrap}>
                            <img src="/gcash-qr.png" alt="GCash QR Code" className={styles.gcashQrImg} />
                            <p className={styles.gcashQrNote}>Transfer fees may apply.</p>
                          </div>
                          <label className={styles.fieldLabel}>
                            GCash Reference Number
                            <input type="text" className={styles.fieldInput} placeholder="e.g. 1234567890" value={balanceGcashRef} onChange={e => setBalanceGcashRef(e.target.value)} maxLength={30} />
                          </label>
                          {balanceError && <p className={styles.errorMsg}>{balanceError}</p>}
                          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.75rem' }}>
                            <button className={styles.cancelBtn} onClick={() => { setSelectedBalance(null); setBalanceError(''); }}>Cancel</button>
                            <button className={styles.saveBtn} onClick={() => handleBalanceSubmit(tx)} disabled={submittingBalance}>{submittingBalance ? 'Submitting…' : 'Submit Balance Payment'}</button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  {alreadySubmitted && !justConfirmed && (
                    <p style={{ color: '#888', marginTop: '0.5rem', fontSize: '14px', fontStyle: 'italic' }}>Balance payment submitted — awaiting admin verification.</p>
                  )}
                </div>
              );
            })}
            <p className={styles.consignNote} style={{ marginTop: '0.5rem' }}>
              Please settle your remaining balance by the due date.
            </p>
          </div>
        )}

        {myTransactions.length > 0 && (
          <div className={styles.card}>
            <h2 className={styles.sectionTitle}>My Transactions</h2>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead><tr><th>Transaction ID</th><th>Order ID</th><th>Amount</th><th>Type</th><th>Date</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {myTransactions.map((tx, i) => (
                    <tr key={tx._docId} className={i % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                      <td>{formatTransactionId(tx._docId, tx.createdAt)}</td>
                      <td>{formatOrderId(tx.orderId)}</td>
                      <td>{fmt(tx.amount)}</td>
                      <td>{tx.type}</td>
                      <td>{formatDate(tx.createdAt)}</td>
                      <td><StatusBadge status={tx.status} /></td>
                      <td><button className={styles.receiptLinkBtn} onClick={() => handleViewHistoryReceipt(tx)}>Receipt</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader title="Payment" description="Complete your supply order payment" />

      {outstandingBalances.length > 0 && (
        <div className={styles.card} style={{ borderLeft: '4px solid #e07b39' }}>
          <h2 className={styles.sectionTitle} style={{ color: '#e07b39' }}>Outstanding Balance</h2>
          {outstandingBalances.map(tx => {
            const alreadySubmitted = !!tx.balanceGcashRef;
            const isSelected = selectedBalance === tx._docId;
            const justConfirmed = balanceConfirmedId === tx._docId;
            return (
              <div key={tx._docId} style={{ marginBottom: '1rem' }}>
                <div className={styles.netRow} style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '0.25rem' }}>
                  <span style={{ fontWeight: 600 }}>Order {formatOrderId(tx.orderId)}</span>
                  <span className={styles.netLabel}>Remaining balance: <strong>{fmt(tx.remainingAmount)}</strong></span>
                  {tx.paymentDue
                    ? <span className={styles.netLabel}>Due date: <strong>{tx.paymentDue}</strong></span>
                    : <span className={styles.netLabel} style={{ fontStyle: 'italic' }}>Due date pending admin review</span>
                  }
                  <span className={styles.netLabel}>Status: <strong>{tx.status}</strong></span>
                </div>

                {justConfirmed && (
                  <p style={{ color: 'green', marginTop: '0.5rem', fontSize: '14px' }}>
                    ✓ Balance payment submitted. Awaiting admin verification.
                  </p>
                )}

                {!alreadySubmitted && !justConfirmed && (
                  <>
                    {!isSelected ? (
                      <button
                        className={styles.confirmBtn}
                        style={{ marginTop: '0.75rem', width: 'auto', padding: '0.5rem 1.25rem' }}
                        onClick={() => { setSelectedBalance(tx._docId); setBalanceGcashRef(''); setBalanceError(''); }}
                      >
                        Pay Remaining Balance
                      </button>
                    ) : (
                      <div style={{ marginTop: '1rem' }}>
                        <p className={styles.gcashInstruction}>
                          Scan the QR code below using your GCash app to send the remaining balance of <strong>{fmt(tx.remainingAmount)}</strong>.
                          Then enter your GCash reference number below.
                        </p>
                        <div className={styles.gcashQrWrap}>
                          <img src="/gcash-qr.png" alt="GCash QR Code" className={styles.gcashQrImg} />
                          <p className={styles.gcashQrNote}>Transfer fees may apply.</p>
                        </div>
                        <label className={styles.fieldLabel}>
                          GCash Reference Number
                          <input
                            type="text"
                            className={styles.fieldInput}
                            placeholder="e.g. 1234567890"
                            value={balanceGcashRef}
                            onChange={e => setBalanceGcashRef(e.target.value)}
                            maxLength={30}
                          />
                        </label>
                        {balanceError && <p className={styles.errorMsg}>{balanceError}</p>}
                        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.75rem' }}>
                          <button className={styles.cancelBtn} onClick={() => { setSelectedBalance(null); setBalanceError(''); }}>Cancel</button>
                          <button className={styles.saveBtn} onClick={() => handleBalanceSubmit(tx)} disabled={submittingBalance}>
                            {submittingBalance ? 'Submitting…' : 'Submit Balance Payment'}
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {alreadySubmitted && !justConfirmed && (
                  <p style={{ color: '#888', marginTop: '0.5rem', fontSize: '14px', fontStyle: 'italic' }}>
                    Balance payment submitted — awaiting admin verification.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!pendingOrder ? (
        <div className={styles.card}>
          <p style={{ textAlign: 'center', color: 'var(--brown-dark)', padding: '2rem 0' }}>
            No pending orders found. Place an order first from the Orders page.
          </p>
        </div>
      ) : alreadyPaid ? (
        <div className={styles.card}>
          <p style={{ textAlign: 'center', color: 'var(--brown-dark)', padding: '2rem 0' }}>
            Payment already submitted for Order <strong>{formatOrderId(orderId)}</strong>. Awaiting admin verification.
          </p>
        </div>
      ) : (
        <>
          {/* Order Summary */}
          <div className={`${styles.card} printArea`}>
            <h2 className={styles.sectionTitle}>Order Summary</h2>
            <p className={styles.orderId}>Order ID: <strong>{formatOrderId(orderId)}</strong></p>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead><tr><th>Product</th><th>Qty</th><th>Unit Price</th><th>Subtotal</th></tr></thead>
                <tbody>
                  {lineItems.map(item => (
                    <tr key={item.name}>
                      <td>{item.name}</td><td>{item.qty}</td>
                      <td>{fmt(item.unit)}</td><td>{fmt(item.subtotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={styles.totalsBlock}>
              <div className={styles.totalRow}><span>Subtotal</span><span>{fmt(subtotal)}</span></div>
              {deliveryFee > 0 && (
                <div className={styles.totalRow}><span>Delivery Fee</span><span>{fmt(deliveryFee)}</span></div>
              )}
              <div className={`${styles.totalRow} ${styles.grandTotal}`}><span>TOTAL</span><span>{fmt(total)}</span></div>
            </div>
          </div>

          {/* Payment Method */}
          <div className={styles.card}>
            <h2 className={styles.sectionTitle}>Select Payment Method</h2>
            <div className={styles.methodGrid}>
              <button type="button" className={`${styles.methodCard} ${method === 'full' ? styles.methodSelected : ''}`} onClick={() => handleMethodSelect('full')}>
                <svg className={styles.methodIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="3"/>
                  <path d="M6 12h.01M18 12h.01"/>
                </svg>
                <span className={styles.methodLabel}>Full Payment</span>
                <span className={styles.methodSub}>Pay the full amount in one transaction</span>
                {method === 'full' && <span className={styles.selectedDot} />}
              </button>
              <button type="button" className={`${styles.methodCard} ${method === 'partial' ? styles.methodSelected : ''}`} onClick={() => handleMethodSelect('partial')}>
                <svg className={styles.methodIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="5" width="20" height="14" rx="2"/>
                  <path d="M2 10h20"/><path d="M6 15h4"/>
                </svg>
                <span className={styles.methodLabel}>Partial Payment</span>
                <span className={styles.methodSub}>Pay a portion upon arrival of goods.<br />Admin sets the due date for the remaining balance.</span>
                {method === 'partial' && <span className={styles.selectedDot} />}
              </button>
            </div>

            {method === 'full' && (
              <>
                <h2 className={styles.sectionTitle} style={{ marginTop: '1.5rem', textAlign: 'center' }}>Select Full Payment Option</h2>
                <div className={styles.methodGrid}>
                  <button type="button" className={`${styles.methodCard} ${fullPaySub === 'cash' ? styles.methodSelected : ''}`} onClick={() => handleFullPaySubSelect('cash')}>
                    <svg className={styles.methodIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="3"/>
                      <path d="M6 12h.01M18 12h.01"/>
                    </svg>
                    <span className={styles.methodLabel}>Cash on Delivery</span>
                    <span className={styles.methodSub}>Pay the full amount in cash upon receiving your supplies</span>
                    {fullPaySub === 'cash' && <span className={styles.selectedDot} />}
                  </button>
                  <button type="button" className={`${styles.methodCard} ${fullPaySub === 'gcash' ? styles.methodSelected : ''}`} onClick={() => handleFullPaySubSelect('gcash')}>
                    <svg className={styles.methodIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="7" y="2" width="10" height="20" rx="2"/>
                      <circle cx="12" cy="18" r="0.8" fill="currentColor" stroke="none"/>
                      <path d="M10 5h4"/>
                    </svg>
                    <span className={styles.methodLabel}>GCash</span>
                    <span className={styles.methodSub}>Pay the full amount digitally via GCash.</span>
                    {fullPaySub === 'gcash' && <span className={styles.selectedDot} />}
                  </button>
                </div>
              </>
            )}
          </div>

          {isGcash && (
            <div className={styles.card}>
              <h2 className={styles.sectionTitle}>GCash Payment</h2>
              <p className={styles.gcashInstruction}>
                Scan the QR code below using your GCash app to send payment of <strong>{fmt(total)}</strong>.
                Then enter your GCash reference number below.
              </p>
              <div className={styles.gcashQrWrap}>
                <img src="/gcash-qr.png" alt="GCash QR Code" className={styles.gcashQrImg} />
                <p className={styles.gcashQrNote}>Transfer fees may apply.</p>
              </div>
              <label className={styles.fieldLabel}>
                GCash Reference Number
                <input
                  type="text"
                  className={styles.fieldInput}
                  placeholder="e.g. 1234567890"
                  value={gcashRef}
                  onChange={e => setGcashRef(e.target.value)}
                  maxLength={30}
                />
              </label>
            </div>
          )}

          {method === 'partial' && (
            <div className={styles.card}>
              <h2 className={styles.sectionTitle}>Partial Payment Details</h2>
              <label className={styles.fieldLabel}>
                Down-payment upon arrival (%)
                <input type="number" className={styles.fieldInput} min={1} max={99} value={downPayPct} onChange={e => setDownPayPct(Number(e.target.value))} />
              </label>
              <div className={styles.netRow}>
                <span className={styles.netLabel}>Initial payment due on arrival:</span>
                <span className={styles.netValue}>{fmt(partialAmount)}</span>
              </div>
              <div className={styles.netRow}>
                <span className={styles.netLabel}>Remaining balance (admin sets due date):</span>
                <span className={styles.netValue}>{fmt(remainingAmount)}</span>
              </div>
              <p className={styles.consignNote}>Due date for the remaining balance will be set by the admin after verification.</p>

              <h2 className={styles.sectionTitle} style={{ marginTop: '1.5rem' }}>GCash Payment</h2>
              <p className={styles.gcashInstruction}>
                Partial payments are processed via GCash. Scan the QR code below using your GCash app to send the initial payment of <strong>{fmt(partialAmount)}</strong>.
                Then enter your GCash reference number below.
              </p>
              <div className={styles.gcashQrWrap}>
                <img src="/gcash-qr.png" alt="GCash QR Code" className={styles.gcashQrImg} />
                <p className={styles.gcashQrNote}>Transfer fees may apply.</p>
              </div>
              <label className={styles.fieldLabel}>
                GCash Reference Number
                <input
                  type="text"
                  className={styles.fieldInput}
                  placeholder="e.g. 1234567890"
                  value={gcashRef}
                  onChange={e => setGcashRef(e.target.value)}
                  maxLength={30}
                />
              </label>
            </div>
          )}
        </>
      )}

      {error && <p className={styles.errorMsg}>{error}</p>}

      {pendingOrder && !alreadyPaid && (
        <button className={styles.confirmBtn} onClick={handleConfirmClick} disabled={submitting}>
          {submitting ? 'Recording…' : 'Record'}
        </button>
      )}

      {/* Transaction history */}
      {myTransactions.length > 0 && (
        <div className={styles.card} style={{ marginTop: '2rem' }}>
          <h2 className={styles.sectionTitle}>My Transaction History</h2>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead><tr><th>Transaction ID</th><th>Order ID</th><th>Amount</th><th>Type</th><th>Date</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {myTransactions.map((tx, i) => (
                  <tr key={tx._docId} className={i % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                    <td>{formatTransactionId(tx._docId, tx.createdAt)}</td><td>{formatOrderId(tx.orderId)}</td>
                    <td>{fmt(tx.amount)}</td><td>{tx.type}</td>
                    <td>{formatDate(tx.createdAt)}</td>
                    <td><StatusBadge status={tx.status} /></td>
                    <td><button className={styles.receiptLinkBtn} onClick={() => handleViewHistoryReceipt(tx)}>Receipt</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {historyReceiptTx && (
        <div className={styles.overlay} onClick={() => { setHistoryReceiptTx(null); setHistoryReceiptOrder(null); }}>
          <div className={styles.modal} style={{ maxWidth: 640, width: '95vw', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            {(() => {
              const tx = historyReceiptTx;
              const order = historyReceiptOrder;
              const txDate = tx.createdAt?.toDate ? tx.createdAt.toDate() : tx.createdAt ? new Date(tx.createdAt) : new Date();
              const txDateStr = txDate.toLocaleDateString('en-PH', { year: 'numeric', month: 'long', day: 'numeric' });
              const txTimeStr = txDate.toLocaleTimeString('en-PH', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
              const txId = formatTransactionId(tx._docId, tx.createdAt);
              const items = (order?.items || []).map(i => ({ name: i.productId, qty: i.qty, unit: i.price, subtotal: i.qty * i.price }));
              const itemsSubtotal = items.reduce((s, i) => s + i.subtotal, 0);
              const txTotal = tx.amount ?? 0;
              const txDelivery = txTotal - itemsSubtotal;
              const vatRate = 0.12;
              const vatableBase = itemsSubtotal / (1 + vatRate);
              const vatAmount = itemsSubtotal - vatableBase;
              const histExportParams = {
                branchName: tx.branchName ?? user.name,
                orderIdStr: formatOrderId(tx.orderId),
                transactionId: txId,
                date: txDateStr,
                time: txTimeStr,
                payment: tx.type,
                gcashRef: tx.gcashRefNumber ?? '',
                lineItems: items,
                vatableBase,
                vatAmount,
                itemsSubtotal,
                deliveryFee: txDelivery > 0 ? txDelivery : 0,
                total: txTotal,
                partial: tx.partialAmount ?? txTotal,
                remaining: tx.remainingAmount ?? 0,
                method: tx.type,
              };
              return (
                <div className={styles.receipt} style={{ boxShadow: 'none', border: 'none', padding: 0 }}>
                  <div className={styles.receiptHeader}>
                    <div className={styles.receiptLogo}>
                      <img src={juanCafeLogo} alt="Juan Cafe Logo" className={styles.receiptLogoImg} />
                    </div>
                    <div className={styles.receiptCompany}>
                      <p className={styles.receiptCompanyName}>JUAN CAFE</p>
                      <p className={styles.receiptCompanyMeta}>Supply Chain Management System</p>
                      <p className={styles.receiptCompanyMeta}>Bukidnon, Philippines</p>
                      <p className={styles.receiptCompanyMeta}>TIN: 000-000-000-000</p>
                    </div>
                  </div>
                  <div className={styles.receiptDocType}>
                    <span className={styles.receiptDocLabel}>OFFICIAL RECEIPT</span>
                    <span className={styles.receiptDocSub}>(Supply Invoice)</span>
                  </div>
                  <div className={styles.receiptMeta}>
                    <div className={styles.receiptMetaLeft}>
                      <div className={styles.receiptMetaRow}><span className={styles.receiptMetaKey}>Sold To:</span><span className={styles.receiptMetaVal}>{tx.branchName ?? user.name}</span></div>
                      <div className={styles.receiptMetaRow}><span className={styles.receiptMetaKey}>Order No.:</span><span className={styles.receiptMetaVal}>{formatOrderId(tx.orderId)}</span></div>
                      <div className={styles.receiptMetaRow}><span className={styles.receiptMetaKey}>Transaction No.:</span><span className={styles.receiptMetaVal}>{txId}</span></div>
                    </div>
                    <div className={styles.receiptMetaRight}>
                      <div className={styles.receiptMetaRow}><span className={styles.receiptMetaKey}>Date:</span><span className={styles.receiptMetaVal}>{txDateStr}</span></div>
                      <div className={styles.receiptMetaRow}><span className={styles.receiptMetaKey}>Time:</span><span className={styles.receiptMetaVal}>{txTimeStr}</span></div>
                      <div className={styles.receiptMetaRow}><span className={styles.receiptMetaKey}>Payment:</span><span className={styles.receiptMetaVal}>{tx.type}</span></div>
                      {tx.gcashRefNumber && <div className={styles.receiptMetaRow}><span className={styles.receiptMetaKey}>GCash Ref.:</span><span className={styles.receiptMetaVal}>{tx.gcashRefNumber}</span></div>}
                    </div>
                  </div>
                  {items.length > 0 ? (
                    <table className={styles.receiptTable}>
                      <thead><tr>
                        <th className={styles.receiptTh}>Description</th>
                        <th className={`${styles.receiptTh} ${styles.receiptThCenter}`}>Qty</th>
                        <th className={`${styles.receiptTh} ${styles.receiptThRight}`}>Unit Price</th>
                        <th className={`${styles.receiptTh} ${styles.receiptThRight}`}>Amount</th>
                      </tr></thead>
                      <tbody>
                        {items.map((item, idx) => (
                          <tr key={idx} className={idx % 2 === 0 ? styles.receiptRowEven : styles.receiptRowOdd}>
                            <td className={styles.receiptTd}>{item.name}</td>
                            <td className={`${styles.receiptTd} ${styles.receiptTdCenter}`}>{item.qty}</td>
                            <td className={`${styles.receiptTd} ${styles.receiptTdRight}`}>{fmt(item.unit)}</td>
                            <td className={`${styles.receiptTd} ${styles.receiptTdRight}`}>{fmt(item.subtotal)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px', padding: '12px 0' }}>Loading order items…</p>
                  )}
                  <div className={styles.receiptTotals}>
                    {items.length > 0 && <>
                      <div className={styles.receiptTotalRow}><span>Subtotal (VATable)</span><span>{fmt(Math.round(vatableBase))}</span></div>
                      <div className={styles.receiptTotalRow}><span>VAT (12%)</span><span>{fmt(Math.round(vatAmount))}</span></div>
                      <div className={styles.receiptTotalRow}><span>Goods Total</span><span>{fmt(itemsSubtotal)}</span></div>
                      {txDelivery > 0 && <div className={styles.receiptTotalRow}><span>Delivery Fee</span><span>{fmt(txDelivery)}</span></div>}
                    </>}
                    <div className={`${styles.receiptTotalRow} ${styles.receiptGrandTotal}`}><span>TOTAL AMOUNT DUE</span><span>{fmt(txTotal)}</span></div>
                    {tx.type === 'Partial Payment' && <>
                      <div className={`${styles.receiptTotalRow} ${styles.receiptPaidRow}`}><span>Amount Paid (Upon Arrival)</span><span>{fmt(tx.partialAmount)}</span></div>
                      <div className={`${styles.receiptTotalRow} ${styles.receiptBalanceRow}`}><span>Remaining Balance</span><span>{fmt(tx.remainingAmount)}</span></div>
                    </>}
                  </div>
                  <div className={styles.receiptFooter}>
                    <p className={styles.receiptFooterNote}>This document is not valid for claiming input taxes.</p>
                    <p className={styles.receiptFooterNote}>This serves as your Official Receipt for the above transaction.</p>
                    <p className={styles.receiptFooterThank}>Thank you for your business!</p>
                    <div className={styles.receiptSigBlock}>
                      <div className={styles.receiptSig}><div className={styles.receiptSigLine} /><span className={styles.receiptSigLabel}>Prepared by / Cashier</span></div>
                      <div className={styles.receiptSig}><div className={styles.receiptSigLine} /><span className={styles.receiptSigLabel}>Received by / Branch</span></div>
                    </div>
                    <p className={styles.receiptStatus}>Status: <strong>{tx.status}</strong></p>
                  </div>
                  <div className={styles.receiptActions}>
                    {items.length > 0 && (
                      <button className={styles.exportBtn} onClick={() => handleExport(histExportParams)}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6, verticalAlign: 'middle' }}>
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                          <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                        Export
                      </button>
                    )}
                    <button className={styles.backBtn} onClick={() => { setHistoryReceiptTx(null); setHistoryReceiptOrder(null); }}>Close</button>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {showModal && (
        <div className={styles.overlay} onClick={() => setShowModal(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>Confirm Payment</h3>
            <p className={styles.modalWarning}>
              <strong>Important:</strong> By confirming, you acknowledge that your branch has not yet ordered or paid for this product through any other channel. Submitting a duplicate or incorrect payment record is the sole responsibility of the branch. This action cannot be undone.
            </p>
            <p className={styles.modalBody}>
              You are about to submit a <strong>{method === 'partial' ? 'Partial Payment' : isGcash ? 'GCash' : 'Cash on Delivery'}</strong> for
              {' '}Order <strong>{formatOrderId(orderId)}</strong> totalling <strong>{fmt(total)}</strong>.
              {method === 'partial' && <><br />Initial payment on arrival: <strong>{fmt(partialAmount)}</strong>. Remaining: <strong>{fmt(remainingAmount)}</strong>.</>}
              {needsGcashRef && <><br />GCash Reference No.: <strong>{gcashRef}</strong></>}
              {' '}Proceed?
            </p>
            <div className={styles.modalActions}>
              <button className={styles.cancelBtn} onClick={() => setShowModal(false)}>Cancel</button>
              <button className={styles.saveBtn} onClick={handleConfirm}>Confirm</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
